import { useEffect, useMemo, useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExtendedThalamosUser,
  ExternalPatientLinkEnhanced,
  FileNote,
  FormContextData,
  FormContextMiniForm,
  UUID,
} from "@thalamos/aspire-common";
import { getLatestVersion } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { useGetFileNotes } from "../../../hooks/apiCalls.js";
import { routeFns } from "../../../routePaths.js";
import { CancelDeMinimisDialog } from "../admission/CancelDeMinimisDialog.js";
import { DeMinimisResponseModal } from "../admission/DeMinimisResponseModal.js";
import { singleFormContextLogic } from "../helpers/formContextLogic.js";
import { FormRow } from "../helpers/FormRow.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  calculateWorkItemStatus,
  FormTitle,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";

export type StandaloneFormRowProps = {
  formContext: FormContextData;
  form: FormContextMiniForm;
  setPdfViewFormId: (formId: string | null) => void;
  user: ExtendedThalamosUser;
  showFinaliseConfirmation: (fullFormName: string) => void;
  showMhaStatusConfirmation: (fullFormName: string) => void;
  showDeleteConfirmation: (formId: UUID) => void;
  showRequestDeMinimisDialog?: (formId: UUID, formTemplateId: string) => void;
  reloadFormContext: () => void;
  lastMerged?: string | undefined;

  // Scrutiny is offered per form by default. Contexts that are scrutinised as a
  // whole suppress it here and offer it once for the whole context instead.
  offerScrutiny?: boolean;

  testId?: string;
};

export function StandaloneFormRow({
  formContext,
  form,
  setPdfViewFormId,
  user,
  showFinaliseConfirmation,
  showMhaStatusConfirmation,
  showDeleteConfirmation,
  showRequestDeMinimisDialog,
  reloadFormContext,
  lastMerged,
  offerScrutiny = true,
  testId = "standalone-form-row",
}: StandaloneFormRowProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const formDraftId = useMemo(() => v4(), []);

  const { t } = useTranslation();

  const externalLinks = useExternalPatientLinks({
    patientId: formContext.patientId,
  });

  const [showForm, setShowForm] = useState(true);

  const {
    fullFormName,
    draftIdOnNextPart,
    canLaunchDraft,
    formNeedsFinalise,
    canDownloadForm,
    canUploadFormToExternalSystem,
    canFinaliseForm,
    canSendForm,
    canDeleteForm,
    canContinueForm,
    canRequestDeMinimis,
  } = singleFormContextLogic(formContext, form, user);

  const uploadToRioExternalLink:
    ExternalPatientLinkEnhanced | null | undefined = useMemo(() => {
    if (canUploadFormToExternalSystem === false) {
      return null;
    }

    if (externalLinks.externalPatientLinksLoading === true) {
      return undefined;
    }

    const candidateLinks = (
      externalLinks.externalPatientLinks?.externalLinks ?? []
    ).filter(
      (externalPatientLink) => externalPatientLink.externalSystemType === "rio",
    );

    // The lowest number is the highest priority (i.e. 1 is higher priority than 2)
    const highestProirityLink = candidateLinks
      .filter((externalPatientLink) => externalPatientLink.canPush)
      .sort(
        (a, b) =>
          a.priorityWithinExternalSystem - b.priorityWithinExternalSystem,
      )?.[0];

    return highestProirityLink ?? null;
  }, [
    canUploadFormToExternalSystem,
    externalLinks.externalPatientLinks?.externalLinks,
    externalLinks.externalPatientLinksLoading,
  ]);

  const [uploadedToRioExternalLink, setUploadedToRioExternalLink] = useState<
    { uploaded: false } | { uploaded: true; date: string }
  >({ uploaded: false });

  useEffect(() => {
    const doEffect = async () => {
      if (uploadToRioExternalLink) {
        const result = await externalLinks.getExternalPatientFormPushEvent(
          uploadToRioExternalLink,
          formContext.id,
          form.id,
        );
        if (result !== null) {
          setUploadedToRioExternalLink({
            uploaded: true,
            date: result.created,
          });
        }
      }
    };

    doEffect();
  }, [
    externalLinks.externalPatientLinksLoading,
    externalLinks.getExternalPatientFormPushEvent,
    formContext.id,
    uploadToRioExternalLink,
  ]);

  // The row reports the status of its own form, so the status calculation is
  // given that form alone rather than everything in the context.
  const summary = calculateWorkItemStatus(theme, {
    formContextType: formContext.type,
    forms: [form],
    otherWorkItems: formContext.workItems,
  });

  const { fileNotes, reloadFileNotes } = useGetFileNotes(form.id, user);

  const hasFileNotes = fileNotes && fileNotes.length > 0;

  // Find active de minimis file note targeting current user
  const activeDeMinimisNote: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus) &&
          fn.deMinimisTargetUserId === user.id,
      ),
    [fileNotes, user.id],
  );

  // Find de minimis note targeting another user (for info banner)
  const deMinimisNoteForOtherUser: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus) &&
          fn.deMinimisTargetUserId &&
          fn.deMinimisTargetUserId !== user.id,
      ),
    [fileNotes, user.id],
  );

  const deMinimisTargetUserName = useMemo(() => {
    if (!deMinimisNoteForOtherUser?.deMinimisTargetUserId) return undefined;
    const sig = getLatestVersion(form).signatures.find(
      (s) => s.userId === deMinimisNoteForOtherUser.deMinimisTargetUserId,
    );
    return sig?.userName ?? "the assigned user";
  }, [deMinimisNoteForOtherUser, form]);

  // Find any active de minimis note on this form (for MHA admin cancel)
  const activeDeMinimisForCancel: FileNote | undefined = useMemo(
    () =>
      fileNotes?.find(
        (fn) =>
          fn.deMinimisStatus &&
          ["requested", "accepted"].includes(fn.deMinimisStatus),
      ),
    [fileNotes],
  );

  const [showDeMinimisResponseModal, setShowDeMinimisResponseModal] =
    useState(false);
  const [showCancelDeMinimisDialog, setShowCancelDeMinimisDialog] =
    useState(false);

  // Auto-open modal when current user has an active de minimis correction
  const hasAutoOpenedDeMinimisRef = useRef(false);
  useEffect(() => {
    if (activeDeMinimisNote && !hasAutoOpenedDeMinimisRef.current) {
      hasAutoOpenedDeMinimisRef.current = true;
      setShowDeMinimisResponseModal(true);
    }
  }, [activeDeMinimisNote]);

  useEffect(() => {
    reloadFileNotes();
  }, [formContext, form.id]);

  const formHasMoreThanOneSignature =
    getLatestVersion(form).signatures?.length > 0;

  const subheading = `${
    formHasMoreThanOneSignature
      ? `Signed by: ${form.versions
          .at(-1)!
          .signatures.map((s) => s.userName)
          .join(", ")}.`
      : ""
  }  Last updated:
    ${dayjs.utc(form.updated || formContext.updated).fromNow()}`;

  const showEditMhaStatus = userCanEditMhaStatus(user);

  const menuOptions: MenuOptionsType[] = [
    ...(canDownloadForm
      ? [
          {
            icon: "view",
            onClick: async () => {
              setPdfViewFormId(form.id);
            },
            name: "View PDF",
            disabled: false,
          },
          {
            name: "Download",
            onClick: async () => {
              const result = await api.forms.getPdf(
                form.id,
                formContext.type === "admission",
              );
              if (result.status === 200) {
                const dataUri = `data:application/pdf;base64,${result.data}`;
                triggerDownload(dataUri, "form.pdf");
              }
            },
            icon: "download",
            disabled: false,
          },
        ]
      : []),
    ...(uploadToRioExternalLink !== null
      ? [
          {
            name: "Upload to Rio",
            icon: uploadedToRioExternalLink.uploaded ? "uploadDone" : "upload",
            tooltip: uploadedToRioExternalLink.uploaded
              ? [
                  "Uploaded to Rio",
                  dayjs(uploadedToRioExternalLink.date).format(
                    "DD/MM/YYYY HH:mm",
                  ),
                ]
              : undefined,
            onClick: async () => {
              if (uploadToRioExternalLink) {
                await externalLinks.uploadFormPdfViaExternalPatientLink(
                  uploadToRioExternalLink,
                  [form.id],
                  formContext,
                );
              }
            },
            disabled: uploadToRioExternalLink === undefined,
          },
        ]
      : []),
    ...(canDeleteForm
      ? [
          {
            name: "Delete",
            onClick: async () => {
              showDeleteConfirmation(form.id);
            },
            icon: "delete",
            disabled: false,
          },
        ]
      : []),
    ...(canLaunchDraft
      ? [
          {
            name: "Start",
            onClick: async () => {
              const result = await api.drafts.create(formDraftId, {
                formId: form.id,
              });
              if (result.status === 201) {
                navigate(
                  routeFns.formDraftsComplete(
                    formDraftId,
                    formContext.patientId,
                  ),
                );
              } else {
                renderErrorToast({ message: "Failed to create draft" });
              }
            },
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(canContinueForm
      ? [
          {
            name: "Continue",
            link: routeFns.formDraftsComplete(
              draftIdOnNextPart!,
              formContext.patientId,
            ),
            icon: "edit",
            disabled: false,
          },
        ]
      : []),
    ...(canSendForm
      ? [
          {
            name: "Send",
            link: routeFns.formContextPageSendRequest(
              formContext.id,
              formContext.patientId,
              formNeedsFinalise
                ? { requestType: "finalise" }
                : {
                    requestType: "complete",
                    formId: form.id,
                    formPart: getLatestVersion(form).data.length,
                  },
            ),
            icon: "transfer",
            disabled: false,
          },
        ]
      : []),
    ...(canFinaliseForm && offerScrutiny
      ? [
          {
            name: "Complete Scrutiny",
            onClick: () =>
              showEditMhaStatus
                ? showMhaStatusConfirmation(fullFormName ?? "")
                : showFinaliseConfirmation(fullFormName ?? ""),
            icon: "accept",
            disabled: false,
          },
        ]
      : []),
    ...(canRequestDeMinimis &&
    showRequestDeMinimisDialog &&
    !activeDeMinimisForCancel
      ? [
          {
            icon: "edit",
            onClick: () => {
              showRequestDeMinimisDialog(form.id, form.formTemplate.id);
            },
            name: "Request De Minimis",
            disabled: false,
          },
        ]
      : []),
    ...(activeDeMinimisNote
      ? [
          {
            icon: "edit",
            onClick: () => {
              setShowDeMinimisResponseModal(true);
            },
            name: "Respond to De Minimis",
            disabled: false,
          },
        ]
      : []),
    ...(user.sessionContext?.teamType === "mha" && activeDeMinimisForCancel
      ? [
          {
            icon: "delete",
            onClick: () => setShowCancelDeMinimisDialog(true),
            name: "Cancel De Minimis",
            disabled: false,
          },
        ]
      : []),
    ...(user.sessionContext?.teamType === "mha" &&
    form.status !== "finalised" &&
    formHasMoreThanOneSignature
      ? [
          {
            icon: "add",
            onClick: () => {
              return navigate(
                routeFns.formContextAddFileNoteDialog(
                  formContext.id,
                  formContext.patientId,
                  { formId: form.id },
                ),
              );
            },
            name: "Add file note",
            disabled: false,
          },
        ]
      : []),
    ...(user.sessionContext?.teamType === "mha" &&
    form.status !== "finalised" &&
    hasFileNotes
      ? [
          {
            icon: "delete",
            onClick: () => {
              return navigate(
                routeFns.formContextDeleteFileNoteDialog(
                  formContext.id,
                  formContext.patientId,
                  { formId: form.id },
                ),
              );
            },
            name: "Delete file note(s)",
            disabled: false,
          },
        ]
      : []),
  ];

  return (
    <Container addMarginBottom data-testid={testId}>
      <externalLinks.Component />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
        }}
      >
        <FormTitle
          subtitleText={t("common.form")}
          subTitleTextFontSize={2}
          hasContainerMarginBottom={false}
        />
        <ExpandMore
          role="button"
          isExpanded={showForm}
          onClick={() => setShowForm(!showForm)}
          aria-expanded={showForm}
          aria-label={t("components.cardExtended.expandMoreLabel")}
        >
          <ExpandMoreIcon sx={{ color: "primary.main" }} />
        </ExpandMore>
      </Box>
      {deMinimisNoteForOtherUser && deMinimisTargetUserName && (
        <Banner
          title={`A de minimis correction is awaiting action by ${deMinimisTargetUserName}. They need to log in to complete this correction.`}
          bannerType={BannerList.WARNING}
        />
      )}
      {showForm && (
        <Box sx={{ mt: 2 }}>
          <FormRow
            menuOptions={menuOptions ?? []}
            heading={fullFormName ?? ""}
            subHeading={subheading}
            summary={summary}
            lastMerged={lastMerged}
          />
        </Box>
      )}
      {showDeMinimisResponseModal && activeDeMinimisNote && (
        <DeMinimisResponseModal
          fileNote={activeDeMinimisNote}
          formId={form.id}
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          userName={user.name}
          userDefaultSignature={user.defaultSignature}
          onClose={() => {
            setShowDeMinimisResponseModal(false);
            reloadFileNotes();
          }}
        />
      )}
      {showCancelDeMinimisDialog && activeDeMinimisForCancel && (
        <CancelDeMinimisDialog
          formId={form.id}
          fileNoteId={Number(activeDeMinimisForCancel.fileNoteId)}
          onClose={() => setShowCancelDeMinimisDialog(false)}
          onSuccess={() => {
            setShowCancelDeMinimisDialog(false);
            reloadFileNotes();
            reloadFormContext();
          }}
        />
      )}
    </Container>
  );
}
